<template>
	<div class="item custom-upload-item file-upload-item">
		<div class="file-upload-header">
			<span class="label"
				>{{ field.dynamicFieldName }}<span class="required ml6" v-if="field.config == 1">*</span></span
			>
			<van-uploader
				v-show="!showFileList.length"
				:max-count="maxCount"
				v-model="uploaderFileList"
				:max-size="fileSizeLimit"
				@oversize="handleOverLimit"
				:accept="accept"
				:after-read="handleAfterRead"
				:preview-image="false"
				:show-upload="false"
				:deletable="false"
				:result-type="resultType"
			>
				<div class="upload-btn">
					<m-svg-icon name="ignore-add" class="add-icon" />
					<span>{{ $t('上传文件') }}</span>
				</div>
			</van-uploader>
		</div>
		<div class="upload-tip" v-if="field.dynamicFieldDescribed">{{ field.dynamicFieldDescribed }}</div>
		<div class="file-list" v-if="showFileList.length > 0">
			<div class="file-item" v-for="(file, fileIndex) in showFileList" :key="fileIndex">
				<m-svg-icon :name="getFileIcon(file)" class="file-icon" />
				<span class="file-name">{{ file.fileName }}</span>
				<m-svg-icon name="ignore-ope-delete" class="delete-icon" @click.native="handleDelete(file)" />
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, ref, watch, getCurrentInstance } from 'vue';
import { useStore } from '@/utils/vue-api';
import { AliOssUpload } from '@/utils/ali-oss';
import { Uploader as VanUploader } from 'vant';
import { isApp, isIos } from '@/utils/const';

const props = defineProps({
	field: {
		type: Object,
		required: true
	},
	index: {
		type: Number,
		required: true
	}
});

const emit = defineEmits(['update']);

const { proxy } = getCurrentInstance();

const store = useStore();

const acceptTypes = ['pdf', 'doc', 'docx', 'xlsx'];
const maxCount = ref(5);
const fileSizeLimit = ref(1024 * 1024 * 200); // 200MB
const resultType = ref('file');
const uploaderFileList = ref([]);

// 内部文件列表状态
const internalFileList = ref([]);

const accept = computed(() => {
	if (isApp && !isIos) {
		return 'file/*';
	}
	return '.pdf,.doc,.docx,.xlsx';
});

const showFileList = computed(() => {
	return internalFileList.value.filter(item => item.url);
});

// 监听 props 变化，初始化内部文件列表
watch(
	() => props.field.dynamicFieldValue,
	newVal => {
		if (!newVal) {
			internalFileList.value = [];
			return;
		}
		try {
			internalFileList.value = JSON.parse(newVal);
		} catch {
			internalFileList.value = [];
		}
	},
	{ immediate: true }
);

// 更新父组件数据
const updateParent = () => {
	const list = internalFileList.value
		.filter(item => item.url)
		.map(item => ({
			url: item.url,
			fileName: item.fileName
		}));
	emit('update', JSON.stringify(list));
};

const showTooltip = msg => {
	store.dispatch('showTooltip', { msg });
};

const getFileIcon = file => {
	let index = file?.fileName?.lastIndexOf('.');
	let type = file?.fileName?.substring(index)?.replace('.', '');

	if (['doc', 'docx'].includes(type)) {
		type = 'word';
	}
	return `ignore-file-${type}`;
};

// 上传文件
const handleAfterRead = async ({ file }) => {
	const extension = file.name.split('.').pop();

	if (!acceptTypes.includes(extension)) {
		showTooltip(proxy.$t('enterprise.appointment.file-upload-error'));
		return;
	}

	try {
		const res = await AliOssUpload({
			file,
			originName: new Date().getTime() + '.' + extension,
			hideMessage: true,
			showLoading: true
		});

		const info = {
			url: res.viewurl,
			fileName: file.name
		};

		internalFileList.value.push(info);
		updateParent();
	} catch (error) {
		console.error('文件上传失败:', error);
		showTooltip(proxy.$t('enterprise.appointment.file-upload-error-tip'));
	}
};

const handleOverLimit = () => {
	showTooltip(proxy.$t('enterprise.appointment.file-upload-error-size', { size: '200M' }));
};

const handleDelete = file => {
	internalFileList.value = internalFileList.value.filter(item => item.url !== file.url);
	updateParent();
};
</script>

<style lang="less" scoped>
.item {
	padding: 0.14rem 0;
	display: flex;
	align-items: center;
	justify-items: center;
	font-size: 0.15rem;
	color: #333;

	&:not(:last-child) {
		border-bottom: 0.5px solid #e8eaef;
	}

	&.custom-upload-item {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		padding: 0.14rem 0;

		&.file-upload-item {
			.file-upload-header {
				display: flex;
				align-items: center;
				width: 100%;
				margin-bottom: 0.04rem;
				gap: 0.04rem;
				height: 0.22rem;

				.label {
					flex: 1;
				}

				/deep/ .van-uploader {
					flex-shrink: 0;
				}

				.upload-btn {
					display: flex;
					align-items: center;
					justify-content: center;
					flex-flow: row nowrap;
					gap: 0.04rem;

					.add-icon {
						color: #3f5bd6;
						font-size: 0.22rem;
					}

					span {
						color: #666;
						font-size: 0.14rem;
					}
				}
			}

			.upload-tip {
				margin-top: 0.04rem;
				width: 100%;
				line-height: 1;
				font-size: 0.12rem;
				color: #989898;
			}

			.file-list {
				margin-top: 0.16rem;
				width: 100%;
				display: flex;
				flex-direction: column;
				gap: 0.16rem;

				.file-item {
					display: flex;
					align-items: center;
					justify-content: flex-start;
					flex-flow: row nowrap;
					min-height: 0.26rem;
					gap: 0.12rem;

					.file-icon {
						width: 0.2rem;
						height: 0.24rem;
						flex-basis: 0.2rem;
					}

					.file-name {
						font-size: 0.14rem;
						color: #333;
						line-height: 1.2;
						flex: 1;
						word-break: break-all;
					}

					.delete-icon {
						width: 0.2rem;
						height: 0.2rem;
						color: #999;
						flex-basis: 0.2rem;
					}
				}
			}
		}
	}

	.ml6 {
		margin-left: 0.06rem;
	}

	.required {
		color: #eb5149;
		font-size: 0.15rem;
		font-weight: 400;
		vertical-align: middle;
	}
}
</style>
